import { Mail, ArrowUpRight, Check } from "lucide-react";
import WhatsAppIcon from "@/components/icons/WhatsAppIcon";
import useSEO from "@/hooks/useSEO";
import { useQuoteForm } from "@/hooks/useQuoteForm";
import { COMPANY, countries, REGIONAL_CONTACTS } from "@/data/clientsData";
import { organizationSchema, breadcrumbSchema, localBusinessSchema } from "@/lib/seo-schemas";

const ContactPage = () => {
  useSEO({
    title: "Contact Stagehook Global · Get Your Expo Booth Quote in 72h",
    description:
      "Tell us your expo, country and stand size — Stagehook Global will send concept renders and a budget quote within 72 hours.",
    keywords:
      "exhibition stall quote, expo booth quote, contact expo booth builder, get booth design quote",
    path: "/contact",
    jsonLd: [
      organizationSchema(),
      localBusinessSchema(),
      breadcrumbSchema([
        { name: "Home", path: "/" },
        { name: "Contact", path: "/contact" },
      ]),
    ],
  });

  const { form, submitted, submitting, error, onChange, onSubmit } = useQuoteForm();

  return (
    <main data-testid="contact-page" className="pt-[72px]">
      <section className="py-20 grain relative">
        <div className="hero-blob" style={{ top: "-150px", left: "-200px", opacity: 0.2 }} />
        <div className="shg-container relative z-10 grid lg:grid-cols-[1.1fr_1fr] gap-12 items-center">
          <div className="max-w-3xl">
            <div className="shg-eyebrow mb-5">Get a quote</div>
            <h1 className="font-display text-6xl md:text-7xl leading-[0.92] mb-6">
              Tell us about<br />
              your <span style={{ color: "var(--red)" }}>next expo</span>.
            </h1>
            <p className="text-lg text-[var(--text-soft)] max-w-xl">
              Concept renders and a budget estimate in your inbox within 72 hours.
            </p>
          </div>
          <div data-testid="contact-hero-visual" className="hidden lg:block relative">
            <div className="aspect-[4/3] rounded-2xl overflow-hidden" style={{ border: "1px solid var(--line-strong)" }}>
              <img src="/projects/smm-hamburg/smm-hamburg-2.webp" alt="Stagehook Global stand handover at SMM Hamburg 2026" className="w-full h-full object-cover" />
            </div>
            <div className="absolute -bottom-6 left-6 right-6 grid grid-cols-3 rounded-xl overflow-hidden backdrop-blur-xl" style={{ background: "rgba(16,16,20,0.9)", border: "1px solid var(--line-strong)" }}>
              {[[COMPANY.projectsBuilt, "Stands delivered"], ["2015", "Building since"], ["72h", "3D design + quote"]].map(([n, l]) => (
                <div key={l} data-testid={`contact-trust-${l.split(" ")[0].toLowerCase()}`} className="p-4 text-center">
                  <div className="font-display text-2xl" style={{ color: "var(--red)" }}>{n}</div>
                  <div className="font-mono text-[9px] tracking-[0.2em] text-[var(--text-muted)] uppercase mt-1">{l}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="pb-24">
        <div className="shg-container grid lg:grid-cols-12 gap-10">
          {/* Form */}
          <div className="lg:col-span-7">
            {submitted ? (
              <div
                data-testid="form-success"
                className="p-12 rounded-2xl text-center"
                style={{ background: "var(--surface)", border: "1px solid var(--red)" }}
              >
                <div
                  className="w-16 h-16 rounded-full mx-auto mb-6 flex items-center justify-center"
                  style={{ background: "var(--red)" }}
                >
                  <Check size={28} />
                </div>
                <h2 className="font-display text-3xl mb-3">Thank you, {form.name || "friend"}!</h2>
                <p className="text-[var(--text-soft)] max-w-md mx-auto">
                  Your request is in. Our team will reach out within 24 hours with next steps and concept directions.
                </p>
              </div>
            ) : (
              <form
                onSubmit={onSubmit}
                data-testid="quote-form"
                className="grid md:grid-cols-2 gap-5 p-8 md:p-10 rounded-2xl"
                style={{ background: "var(--surface)", border: "1px solid var(--line-strong)" }}
              >
                <Field label="Your name" name="name" required value={form.name} onChange={onChange} />
                <Field label="Email" name="email" type="email" required value={form.email} onChange={onChange} />
                <Field label="Phone / WhatsApp" name="phone" value={form.phone} onChange={onChange} />
                <Field label="Company" name="company" value={form.company} onChange={onChange} />
                <SelectField
                  label="Country"
                  name="country"
                  value={form.country}
                  onChange={onChange}
                  options={countries.map((c) => ({ value: c.slug, label: c.name }))}
                />
                <Field label="Expo / Event" name="expo" value={form.expo} onChange={onChange} placeholder="e.g. Interzoo 2026" />
                <Field label="Stand size (sqm)" name="size" value={form.size} onChange={onChange} placeholder="e.g. 100 sqm" />
                <div className="md:col-span-2">
                  <label className="font-mono text-[10px] tracking-[0.2em] text-[var(--text-muted)] uppercase block mb-2">
                    Your brief
                  </label>
                  <textarea
                    data-testid="form-message"
                    name="message"
                    value={form.message}
                    onChange={onChange}
                    rows={5}
                    placeholder="Tell us about your brand, goals and stand ideas..."
                    className="w-full bg-transparent border rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-[var(--red)] transition-colors"
                    style={{ borderColor: "var(--line-strong)" }}
                  />
                </div>
                {/* Honeypot: real users don't see or fill this */}
                <input
                  type="text"
                  name="website"
                  value={form.website}
                  onChange={onChange}
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  style={{ position: "absolute", left: "-10000px", width: "1px", height: "1px", opacity: 0 }}
                />
                {error && (
                  <div
                    data-testid="form-error"
                    className="md:col-span-2 p-4 rounded-lg text-sm"
                    style={{ background: "rgba(255,42,42,0.08)", border: "1px solid var(--red)", color: "#ffb3b3" }}
                  >
                    {error}
                  </div>
                )}
                <div className="md:col-span-2 flex flex-col md:flex-row md:items-center justify-between gap-4 pt-2">
                  <p className="text-xs text-[var(--text-muted)]">
                    We&apos;ll never share your details. Replies within 24 hours. See our <a href="/privacy" className="underline hover:text-[var(--red)]">Privacy Policy</a>.
                  </p>
                  <button
                    type="submit"
                    data-testid="form-submit"
                    className="btn-primary"
                    disabled={submitting}
                    style={submitting ? { opacity: 0.7, cursor: "wait" } : undefined}
                  >
                    {submitting ? "Sending…" : (<>Send Request <ArrowUpRight size={16} /></>)}
                  </button>
                </div>
              </form>
            )}
          </div>

          {/* Contact details */}
          <aside className="lg:col-span-5 space-y-4">
            <div>
              <div className="font-mono text-[10px] tracking-[0.25em] text-[var(--text-muted)] mb-3">
                DIRECT REGIONAL LINES
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {REGIONAL_CONTACTS.map((rc) => (
                  <a
                    key={rc.region}
                    href={`tel:${rc.dial}`}
                    data-testid={`contact-phone-${rc.region.toLowerCase()}`}
                    className="p-5 rounded-xl transition-all hover:border-[var(--red)] flex flex-col"
                    style={{ background: "var(--surface)", border: "1px solid var(--line)" }}
                  >
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-2xl">{rc.flag}</span>
                      <span className="font-mono text-[10px] tracking-[0.2em] text-[var(--text-muted)]">
                        {rc.region.toUpperCase()}
                      </span>
                    </div>
                    <div className="font-display text-lg leading-tight mb-2">{rc.phone}</div>
                    {rc.address && (
                      <div className="text-[11px] leading-snug text-[var(--text-muted)] mt-auto">
                        {rc.address}
                      </div>
                    )}
                  </a>
                ))}
              </div>
            </div>

            <ContactCard icon={WhatsAppIcon} label="WhatsApp" value={COMPANY.whatsapp} href={`https://wa.me/${COMPANY.whatsapp.replace(/\D/g, "")}`} />
            <ContactCard icon={Mail} label="Email us" value={COMPANY.email} href={`mailto:${COMPANY.email}`} />
          </aside>
        </div>
      </section>
    </main>
  );
};

const Field = ({ label, name, type = "text", required = false, value, onChange, placeholder }) => (
  <div>
    <label className="font-mono text-[10px] tracking-[0.2em] text-[var(--text-muted)] uppercase block mb-2">
      {label}
    </label>
    <input
      data-testid={`field-${name}`}
      name={name}
      type={type}
      required={required}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      className="w-full bg-transparent border rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-[var(--red)] transition-colors"
      style={{ borderColor: "var(--line-strong)" }}
    />
  </div>
);

const SelectField = ({ label, options, name, value, onChange }) => (
  <div>
    <label className="font-mono text-[10px] tracking-[0.2em] text-[var(--text-muted)] uppercase block mb-2">
      {label}
    </label>
    <select
      data-testid={`field-${name}`}
      name={name}
      value={value}
      onChange={onChange}
      className="w-full bg-transparent border rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-[var(--red)] transition-colors"
      style={{ borderColor: "var(--line-strong)", backgroundColor: "var(--surface)" }}
    >
      <option value="">Select country</option>
      {options.map((o) => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
  </div>
);

const ContactCard = ({ icon: Icon, label, value, href }) => {
  const inner = (
    <div
      className="p-7 rounded-2xl flex items-start gap-5 transition-colors"
      style={{ background: "var(--surface)", border: "1px solid var(--line)" }}
    >
      <div
        className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
        style={{ background: "var(--red-soft)", color: "var(--red)" }}
      >
        <Icon size={20} />
      </div>
      <div>
        <div className="font-mono text-[10px] tracking-[0.25em] text-[var(--text-muted)] uppercase mb-1">{label}</div>
        <div className="font-display text-xl">{value}</div>
      </div>
    </div>
  );
  return href ? <a href={href} className="block hover:opacity-90">{inner}</a> : inner;
};

export default ContactPage;
